import { Button } from "@admin/components/atoms/Button";
import { CustomDateRangePickerBody } from "@admin/components/molecules/Picker";
import useClickOutside from "@admin/hooks/useClickOutside";
import { getDateRange, Periods,Range } from "@enos-monorepo/lib";
import { subDays } from "date-fns";
import { useCallback, useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { CustomPeriodItem } from "./CustomPeriodItem";

interface CustomPeriodProps {
  includeToday?: boolean;
  onSelectRange: (range: Range) => void;
  selectedDateRange?: Range;
  customSelectPanelAlign?: "left" | "right";
}

export function CustomPeriod({
  includeToday = true,
  onSelectRange,
  selectedDateRange,
  customSelectPanelAlign = "left",
}: CustomPeriodProps) {
  const { t, i18n } = useTranslation();
  const [show, setShow] = useState(false);
  const [showWeekPicker, setShowWeekPicker] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange>();

  const selectRange = useCallback(
    (period: Periods) => {
      const range = getDateRange(period, includeToday);
      onSelectRange(range);
      setShowWeekPicker(false);
      setShow(false);
      setSelectedRange(undefined);
    },
    [includeToday, onSelectRange],
  );

  const ref = useRef(null);

  useClickOutside(ref, () => {
    if (selectedRange?.from && selectedRange?.to) {
      onSelectRange({
        start: selectedRange.from,
        end: selectedRange.to,
      });
    }
    setShowWeekPicker(false);
    setShow(false);
  });

  return (
    <div ref={ref} className="relative dropdown">
      <Button
        variant="light"
        size="sm"
        onClick={() => setShow((prev) => !prev)}
        className="dropdown-toggle"
      >
        <p className="text-[13px]">
          {Intl.DateTimeFormat(i18n.language, {
            year: "numeric",
            month: "short",
            day: "numeric",
          }).format(selectedDateRange?.start)}{" "}
          -{" "}
          {Intl.DateTimeFormat(i18n.language, {
            year: "numeric",
            month: "short",
            day: "numeric",
          }).format(selectedDateRange?.end)}
        </p>
        {show ? (
          <i className="ki-filled ki-up"></i>
        ) : (
          <i className="ki-filled ki-down"></i>
        )}
      </Button>
      {show && (
        <div className="absolute dropdown-content w-full left-0 flex z-40 top-full">
          <div className="menu menu-default flex flex-col w-full">
            {includeToday && (
              <CustomPeriodItem
                onClick={() => selectRange(Periods.Today)}
                title={t("shift.Today")}
              />
            )}
            <CustomPeriodItem
              onClick={() => selectRange(Periods.Yesterday)}
              title={t("shift.Yesterday")}
            />
            <CustomPeriodItem
              onClick={() => selectRange(Periods.ThisWeek)}
              title={t("shift.This Week")}
            />
            <CustomPeriodItem
              onClick={() => selectRange(Periods.PastWeek)}
              title={t("shift.Past Week")}
            />
            <CustomPeriodItem
              onClick={() => selectRange(Periods.Last7Days)}
              title={t("shift.Last 7 Days")}
            />
            <CustomPeriodItem
              onClick={() => selectRange(Periods.ThisMonth)}
              title={t("shift.This Month")}
            />
            <CustomPeriodItem
              onClick={() => selectRange(Periods.PastMonth)}
              title={t("shift.Past Month")}
            />
            <CustomPeriodItem
              onClick={() => selectRange(Periods.Last30Days)}
              title={t("shift.Last 30 Days")}
            />
            <div className="relative">
              <CustomPeriodItem
                title={t("shift.Custom")}
                onClick={() => setShowWeekPicker((prev) => !prev)}
              />
              {showWeekPicker && (
                <div
                  className={`z-40 bottom-0 absolute bg-white shadow-lg border border-gray-200 p-2 rounded-xl 
                    ${customSelectPanelAlign === "right" ? "left-full" : "right-full"}`}
                >
                  <CustomDateRangePickerBody
                    disabledDate={{
                      after: subDays(new Date(), includeToday ? 0 : 1),
                    }}
                    setDateRange={setSelectedRange}
                    selectedRange={selectedRange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
